import {
  ButtonColor,
  ButtonSize,
  SearchParams,
  SoldFilterParams,
  Icons,
} from '../enums';
import styled from 'styled-components';
import tw from 'twin.macro';
import { Button, Checkbox, FilterItemWrapper, Input, TagContainer, Popup, Icon } from '.';
import strings from '../language/Swedish';
import { Fragment, useRef, useState } from 'react';
import {
  CheckboxItem,
  CoordinatesType,
  FilterDateItem,
  FilterMinMaxItem,
  FilterTagItem,
} from '../types/dataTypes';
import {
  RoundButtonThatCanHide
} from '@/styles/MapAttributesStyles';
import useHousingAssociation from '../api/mutations/useHousingAssociation';
import { getHousingAssociationReport } from '../api/queries/useHousingAssociationReport';
import { getWGS84fromRT90 } from '../helpers';
import DateItemFilter from './DateItemFilter';
import MinMaxInputFilter from './MinMaxInputFilter';

const AnchorWrapper = styled.div`
  anchor-name: --filterAnchor;
  width: fit-content;
`;
const StyledFilters = styled.div<{ $isMobileLandscape?: boolean }>`
  ${tw`flex flex-col w-full`}

  h2 {
    ${({ $isMobileLandscape }) => $isMobileLandscape && tw`text-base mb-1 mt-0`}
  }
`;

const Container = styled.div<{ $isMobileLandscape?: boolean }>`
  ${tw`grid grid-cols-1 sm:grid-cols-3`}
  ${({ $isMobileLandscape }) => $isMobileLandscape ? tw`grid-cols-2 gap-1` : tw`gap-2`}
`;

const List = styled.div`
  ${tw`flex flex-col gap-1 mx-3 my-4`};
`;

const OrgNumber = styled.div`
  ${tw`flex items-center gap-2 pl-8`};
`;

const StyledInput = styled(Input)`
  ${tw`max-w-40 h-7`};
`;

const StyledError = styled.div`
  ${tw`text-xs text-red-500`};
`;

const LoadingText = styled.span`
  ${tw`text-sm text-gray-600 ml-5`};
`;

const ReportLink = styled.a`
  ${tw`ml-5 flex gap-2 items-center`};
`;

const StyledIcon = styled(Icon)`
  transform: rotate(45deg);
`;

enum FilterParams {
  registered_date_min = 'registered_date_min',
  registered_date_max = 'registered_date_max',
  contract_date_min = 'contract_date_min',
  contract_date_max = 'contract_date_max',
}

const orgNrregex = /^\d{6}-\d{4}$/;

type ErrorType = 'invalid_format' | 'api_error' | 'report_load_failed' | 'report_missing' | null;

interface OrgReportState {
  isLoading: boolean;
  pdfUrl: string | null;
  year: number | null;
  error: ErrorType;
}

interface Props<TFilterParams, TFilterData> {
  setCoordinates: (coordinates: CoordinatesType) => void;
  setIsOpen: (isOpen: boolean) => void;
  setParam: (state: SearchParams, label: TFilterParams, value: any) => void;
  setMultipleParam: (
    state: SearchParams,
    label: TFilterParams,
    value: any
  ) => void; // Alternative setter function
  filterData: TFilterData;
  filterItems: {
    tagItems: FilterTagItem[];
    minMaxItems: FilterMinMaxItem[];
    dateItems: FilterDateItem[];
    checkboxItems: CheckboxItem[];
  };
  isFilterHidden: boolean;
  isOpen: boolean;
  handleResetFilter: () => void;
  handleClose: () => void;
}

const Filters = <
  TFilterParams extends string,
  TFilterData extends Record<string, any>
>({
  setCoordinates,
  setIsOpen,
  setMultipleParam,
  setParam,
  handleResetFilter,
  handleClose,
  filterData,
  filterItems,
  isFilterHidden,
  isOpen
}: Props<TFilterParams, TFilterData>) => {
  const [orgReportState, setOrgReportState] = useState<OrgReportState>({
    isLoading: false,
    pdfUrl: null,
    year: null,
    error: null,
  });
  const inputRef = useRef<HTMLInputElement>(null);
  const firmRef = useRef<HTMLInputElement>(null);
  const filterRef = useRef<any>(null);
  const buttonRef = useRef(null);

  const { error: housingAssociationError, mutate: housingAssociationMutation } =
    useHousingAssociation();

  const updateOrgReportState = (updates: Partial<OrgReportState>) => {
    setOrgReportState(prev => ({ ...prev, ...updates }));
  };

  const getErrorMessage = (errorType: ErrorType): string | null => {
    switch (errorType) {
      case 'invalid_format':
        return strings.error.orgNrInvalid;
      case 'api_error':
        return strings.error.orgNr;
      case 'report_load_failed':
        return strings.error.reportLoadFailed;
      case 'report_missing':
        return strings.error.reportMissing;
      default:
        return null;
    }
  };

  const handleMinMaxChange = (id: string, value: number | Date) => {
    setParam(SearchParams.filter, id as TFilterParams, value);
  };

  const handleResetMinMax = (id: string, resetValue?: number) => {
    const value: null = null;

    if (resetValue !== null && resetValue !== undefined) {
      setParam(SearchParams.filter, id as TFilterParams, resetValue);
    } else {
      setParam(SearchParams.filter, id as TFilterParams, value);
    }
  };

  const handleDateReset = (id: string, reset?: boolean) => {
    if (reset) {
      if (
        id === FilterParams.contract_date_min ||
        id === FilterParams.registered_date_min
      ) {
        setParam(
          SearchParams.filter,
          id as TFilterParams,
          new Date(new Date().setFullYear(new Date().getFullYear() - 1))
        );
      } else if (
        id === FilterParams.contract_date_max ||
        id === FilterParams.registered_date_max
      ) {
        setParam(SearchParams.filter, id as TFilterParams, new Date());
      }
    } else {
      setParam(SearchParams.filter, id as TFilterParams, null);
    }
  };

  const handleTagChange = (id: string, values: string[]) => {
    if (values && values.length > 0)
      setMultipleParam(SearchParams.filter, id as TFilterParams, values);
    else {
      setMultipleParam(SearchParams.filter, id as TFilterParams, null);
    }
  };

  const toggleCheckbox = (id: string) => {
    if (filterData) {
      if (id === 'orgNr') {
        if (filterData[id])
          setMultipleParam(
            SearchParams.filter,
            'owner_id' as TFilterParams,
            ''
          );
      }
      if (id === 'firm') {
        if (filterData[id])
          setMultipleParam(
            SearchParams.filter,
            'estate_agencies' as TFilterParams,
            ''
          );
      }

      setMultipleParam(
        SearchParams.filter,
        id as TFilterParams,
        !filterData[id as TFilterParams]
      );
    }
  };

  const isChecked = (id: TFilterParams) => {
    if (filterData) {
      return filterData[id] ? true : false;
    }
    return false;
  };

  const changeOwnerId = () => {
    const orgNumber = inputRef.current?.value;

    setMultipleParam(
      SearchParams.filter,
      'owner_id' as TFilterParams,
      orgNumber
    );

    if (orgNumber && !orgNrregex.test(orgNumber)) {
      updateOrgReportState({ error: 'invalid_format', pdfUrl: null });
    } else {
      updateOrgReportState({ error: null, pdfUrl: null });
    }
  };

  const changeFirm = (e: React.ChangeEvent<HTMLInputElement>) => {
    const firm = e.target.value;
    setMultipleParam(
      SearchParams.filter,
      'estate_agencies' as TFilterParams,
      firm
    );
  }

  const handleGoToOrgClick = () => {
    const orgNumber = inputRef.current?.value;
    if (orgNumber) {
      updateOrgReportState({ isLoading: true, error: null, pdfUrl: null, year: null });
      housingAssociationMutation(orgNumber, {
        onSuccess: async (data) => {
          if (data) {
            const wgs84Coordinates = getWGS84fromRT90(data.rt90x, data.rt90y);
            const coordinates: CoordinatesType = {
              lat: wgs84Coordinates.lat,
              lng: wgs84Coordinates.lng,
            };
            
            if (!data.reports || data.reports.length === 0) {
              updateOrgReportState({ 
                isLoading: false, 
                pdfUrl: null,
                year: null,
                error: 'report_missing' 
              });
            } else {
              // Find the maximum year from available reports
              const maxYear = Math.max(...data.reports.map(report => report.year));
              
              try {
                // Fetch the PDF for the most recent year
                const pdfUrl = await getHousingAssociationReport(data.id, maxYear);
                updateOrgReportState({
                  isLoading: false, 
                  pdfUrl: pdfUrl,
                  year: maxYear,
                  error: null 
                });
              } catch (error) {
                updateOrgReportState({
                  isLoading: false, 
                  pdfUrl: null,
                  year: maxYear,
                  error: 'report_load_failed' 
                });
              }
            }
            
            setCoordinates(coordinates);
            setMultipleParam(
              SearchParams.filter,
              'owner_id' as TFilterParams,
              orgNumber
            );
          }
        },
        onError: () => {
          updateOrgReportState({ 
            isLoading: false, 
            pdfUrl: null, 
            error: 'api_error' 
          });
        }
      });
    }
  };

  const onPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('Text'); // Get the pasted text
    const newStr = pastedData.replace(/^\s+|\s+$/g, '');
    setMultipleParam(
      SearchParams.filter,
      'estate_agencies' as TFilterParams,
      newStr
    );
    if (firmRef.current) {
      firmRef.current.value = newStr;
    }
  }

  const closeSlot = (
    <>
      <Button
        icon={Icons.refresh}
        size={ButtonSize.medium}
        color={ButtonColor.transparent}
        onClick={handleResetFilter}
      >
        {strings.data.reset}
        </Button>
      <Button
        icon={Icons.cross}
        size={ButtonSize.medium}
        color={ButtonColor.transparent}
        onClick={close}
      />
    </>
  )

  function close() {
    handleClose();
    setIsOpen(false);
  }

  return (
		<>
			<AnchorWrapper>
				<RoundButtonThatCanHide
					icon={Icons.filter}
					ref={buttonRef}
					color={ButtonColor.primary}
					size={ButtonSize.large}
					collapseInMobile
					$isHidden={isFilterHidden}
					onClick={() => setIsOpen(!isOpen)}
				>
					{strings.filter.filter}
				</RoundButtonThatCanHide>
			</AnchorWrapper>
			<Popup
				anchor='--filterAnchor'
				isOpen={isOpen}
				setIsOpen={setIsOpen}
				onClose={close}
				closeSlot={closeSlot}
			>
				<StyledFilters ref={filterRef}>
					<h2>Filter</h2>
					{filterData && (
						<>
							<Container>
								{filterItems.tagItems.map((item) => {
									const selectedItemsArray = filterData[
										item.id as SoldFilterParams
									] as string[];

									return (
										<FilterItemWrapper
											key={item.id}
											label={item.label}
											icon={item.icon}
											handleReset={() =>
												setMultipleParam(
													SearchParams.filter,
													item.id as TFilterParams,
													null
												)
											}
											isActive={
												selectedItemsArray && selectedItemsArray.length > 0
											}
										>
											<TagContainer
												tagItem={item}
												selectedItems={selectedItemsArray}
												handleValueChange={handleTagChange}
											/>
										</FilterItemWrapper>
									);
								})}
								{filterItems.dateItems.map((filterItem) => (
									<DateItemFilter<TFilterData, SoldFilterParams>
										key={`${filterItem.minId}-${filterItem.maxId}`}
										filterItem={filterItem}
										filterData={filterData}
										handleDateReset={handleDateReset}
										handleMinMaxChange={handleMinMaxChange}
									/>
								))}
								{filterItems.minMaxItems.map((filterItem) => (
									<MinMaxInputFilter<TFilterData, SoldFilterParams>
										key={`${filterItem.minId}-${filterItem.maxId}`}
										filterItem={filterItem}
										filterData={filterData}
										handleResetMinMax={handleResetMinMax}
										handleMinMaxChange={handleMinMaxChange}
									/>
								))}
							</Container>
							<List>
								{filterItems.checkboxItems.map(({ id, label }) => (
									<Fragment key={id}>
										<Checkbox
											id={id}
											label={label}
											isChecked={isChecked(id as TFilterParams)}
											onToggle={(id) => toggleCheckbox(id)}
										/>
										{id === 'orgNr' && isChecked(id as TFilterParams) && (
											<OrgNumber>
												<StyledInput
													ref={inputRef}
													value={filterData['owner_id'] as string}
													onChange={changeOwnerId}
													placeholder="XXXXXX-yyyy"
													type="text"
													pattern="^\d{6}-\d{4}$"
												/>
												<Button
													color={ButtonColor.primary}
													size={ButtonSize.medium}
													onClick={handleGoToOrgClick}
												>
													{strings.filter.goToOrg}
												</Button>
												{orgReportState.isLoading && (
													<LoadingText>
														{strings.filter.loadingReport}
													</LoadingText>
												)}
												{orgReportState.pdfUrl && orgReportState.year && (
													<ReportLink
														href={orgReportState.pdfUrl}
														target="_blank"
														rel="noopener noreferrer"
													>
														{`Ladda ned ${strings.filter.report.toLowerCase()}en för ${orgReportState.year}`}
														<StyledIcon icon={Icons.arrowUp} />
													</ReportLink>
												)}
												{(orgReportState.error || housingAssociationError) && (
													<div>
														<StyledError>
															{getErrorMessage(orgReportState.error) || 
															 (housingAssociationError && strings.error.orgNr)}
														</StyledError>
													</div>
												)}
											</OrgNumber>
										)}
										{id === 'firm' && isChecked(id as TFilterParams) && (
											<OrgNumber>
												<StyledInput
													ref={firmRef}
													value={filterData['estate_agencies'] || ''}
													onChange={changeFirm}
													type='text'
													onPaste={onPaste}
												/>
											</OrgNumber>
										)}
									</Fragment>
								))}
							</List>
						</>
					)}
				</StyledFilters>
			</Popup>
		</>
	);
};

export default Filters;
